import { useEffect, useState } from 'react'
import { automationStreamUrl } from '../api/client'

/**
 * AutomationPanel streams (SSE) the ForgeOps run lifecycle for the automation an
 * alert spawned, and renders it: a status stepper, the contract result, and the
 * output. This is where ForgeOps becomes visible — the user sees the capability
 * resolve, run, and finish underneath the alert.
 */

interface RunState {
  run_id?: string
  status?: string
  output?: unknown
  output_contract?: string | null
  error_message?: string | null
}

type Phase = 'connecting' | 'streaming' | 'done' | 'error'

const STEPS = ['queued', 'running', 'done'] as const

function stepIndex(status?: string): number {
  switch (status) {
    case 'created':
    case 'queued':
      return 0
    case 'starting':
    case 'running':
    case 'waiting_external_input':
      return 1
    case 'succeeded':
    case 'failed':
    case 'canceled':
      return 2
    default:
      return -1
  }
}

function isFailed(status?: string): boolean {
  return status === 'failed' || status === 'canceled'
}

export default function AutomationPanel({ alertId }: { alertId: number }) {
  const [run, setRun] = useState<RunState | null>(null)
  const [jobId, setJobId] = useState('')
  const [phase, setPhase] = useState<Phase>('connecting')
  useEffect(() => {
    const es = new EventSource(automationStreamUrl(alertId))

    es.addEventListener('automation', (e) => {
      try {
        setJobId(JSON.parse((e as MessageEvent).data).job_id ?? '')
      } catch {
        /* ignore */
      }
      setPhase('streaming')
    })
    es.addEventListener('state', (e) => {
      try {
        setRun(JSON.parse((e as MessageEvent).data) as RunState)
      } catch {
        /* ignore */
      }
    })
    const finish = () => {
      setPhase('done')
      es.close()
    }
    es.addEventListener('done', finish)
    es.addEventListener('timeout', finish)
    es.onerror = () => {
      // EventSource auto-reconnects; we close to avoid storms. Only surface an
      // error if we never connected.
      setPhase((p) => (p === 'connecting' ? 'error' : p))
      es.close()
    }

    return () => es.close()
  }, [alertId])

  const status = run?.status
  const cur = stepIndex(status)
  const failed = isFailed(status)

  return (
    <div className="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/40 px-4 py-3">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <span className="text-xs font-semibold text-indigo-700">⚙ ForgeOps automation</span>
          {jobId && <span className="text-[11px] font-mono text-gray-400">job {jobId.slice(0, 8)}</span>}
        </div>
        <span className="text-[11px] text-gray-400">
          {phase === 'error' ? 'unavailable' : status ?? 'starting…'}
        </span>
      </div>

      {phase === 'error' && (
        <p className="text-xs text-gray-500">Could not stream the automation (is ForgeOps reachable?).</p>
      )}

      {phase !== 'error' && (
        <>
          {/* status stepper */}
          <div className="flex items-center gap-2">
            {STEPS.map((label, i) => {
              const reached = cur >= i
              const isLast = i === STEPS.length - 1
              const dot = isLast && failed
                ? 'bg-red-500'
                : reached
                  ? 'bg-indigo-500'
                  : 'bg-gray-200'
              const text = isLast && failed
                ? 'text-red-600'
                : reached
                  ? 'text-indigo-700'
                  : 'text-gray-400'
              return (
                <div key={label} className="flex items-center gap-2">
                  <span className={`h-2 w-2 rounded-full ${dot} ${reached && !isLast && cur === i ? 'animate-pulse' : ''}`} />
                  <span className={`text-xs ${text}`}>{isLast && failed ? 'failed' : label}</span>
                  {!isLast && <span className="text-gray-300">→</span>}
                </div>
              )
            })}
          </div>

          {/* contract + error */}
          {run?.output_contract && run.output_contract !== 'ok' && (
            <p className="mt-2 text-[11px] text-amber-700">output contract: {run.output_contract}</p>
          )}
          {run?.output_contract === 'ok' && (
            <p className="mt-2 text-[11px] text-green-600">output contract: ok</p>
          )}
          {failed && run?.error_message && (
            <p className="mt-2 text-xs text-red-600">{run.error_message}</p>
          )}

          {/* output */}
          {status === 'succeeded' && run?.output != null && (
            <pre className="mt-2 max-h-40 overflow-auto rounded bg-white/70 border border-indigo-100 p-2 text-[11px] text-gray-700">
              {JSON.stringify(run.output, null, 2)}
            </pre>
          )}
        </>
      )}
    </div>
  )
}
